import { activeLanguageKeyOf } from "./lang.ts";
import type { ActiveLangKey } from "./lang.ts";
import type { LangKey } from "./lang.ts";

/** Finance UI/Mirror parity is pinned by financeActionTranslations.test.ts. */
export const MIRROR_FINANCE_ACTION_COPY = {
  "en": {
    "review": "Review the selected Finance record and all requested values before confirming in the app. The review has not saved any changes.",
    "blocked": "Make one direct request to create, update or delete a Finance transaction, account or budget. No changes were made.",
    "disabled": "Finance review is temporarily unavailable. No changes were made.",
    "appOnly": "Open the VYV app to review and confirm Finance changes.",
    "createdTransaction": "Transaction created in VYV",
    "updatedTransaction": "Transaction updated in VYV",
    "deletedTransaction": "Transaction deleted from VYV",
    "createdAccount": "Account created in VYV",
    "updatedAccount": "Account updated in VYV",
    "deletedAccount": "Account deleted from VYV",
    "createdBudget": "Budget created in VYV",
    "updatedBudget": "Budget updated in VYV",
    "deletedBudget": "Budget deleted from VYV",
    "unchanged": "The reviewed values were already saved. Nothing changed.",
    "historical": "The original result was verified. This does not confirm the current record."
  },
  "pt": {
    "review": "Revise o registro de Finanças selecionado e todos os valores solicitados antes de confirmar no aplicativo. A revisão ainda não salvou alterações.",
    "blocked": "Faça um pedido direto e único para criar, editar ou excluir uma transação, conta ou orçamento de Finanças. Nenhuma alteração foi feita.",
    "disabled": "A revisão de Finanças está temporariamente indisponível. Nenhuma alteração foi feita.",
    "appOnly": "Abra o aplicativo VYV para revisar e confirmar alterações de Finanças.",
    "createdTransaction": "Transação criada no VYV",
    "updatedTransaction": "Transação atualizada no VYV",
    "deletedTransaction": "Transação excluída do VYV",
    "createdAccount": "Conta criada no VYV",
    "updatedAccount": "Conta atualizada no VYV",
    "deletedAccount": "Conta excluída do VYV",
    "createdBudget": "Orçamento criado no VYV",
    "updatedBudget": "Orçamento atualizado no VYV",
    "deletedBudget": "Orçamento excluído do VYV",
    "unchanged": "Os valores revisados já estavam salvos. Nada mudou.",
    "historical": "O resultado original foi verificado. Isso não confirma o registro atual."
  },
  "nl": {
    "review": "Controleer het geselecteerde financiële gegeven en alle gevraagde waarden voordat je in de app bevestigt. De controle heeft nog geen wijzigingen opgeslagen.",
    "blocked": "Vraag rechtstreeks om één financiële transactie, rekening of budget aan te maken, bij te werken of te verwijderen. Er is niets gewijzigd.",
    "disabled": "De controle van Financiën is tijdelijk niet beschikbaar. Er is niets gewijzigd.",
    "appOnly": "Open de VYV-app om wijzigingen in Financiën te controleren en te bevestigen.",
    "createdTransaction": "Transactie aangemaakt in VYV",
    "updatedTransaction": "Transactie bijgewerkt in VYV",
    "deletedTransaction": "Transactie verwijderd uit VYV",
    "createdAccount": "Rekening aangemaakt in VYV",
    "updatedAccount": "Rekening bijgewerkt in VYV",
    "deletedAccount": "Rekening verwijderd uit VYV",
    "createdBudget": "Budget aangemaakt in VYV",
    "updatedBudget": "Budget bijgewerkt in VYV",
    "deletedBudget": "Budget verwijderd uit VYV",
    "unchanged": "De gecontroleerde waarden waren al opgeslagen. Er is niets gewijzigd.",
    "historical": "Het oorspronkelijke resultaat is geverifieerd. Dit bevestigt de huidige gegevens niet."
  },
  "es": {
    "review": "Revisa el registro de Finanzas seleccionado y todos los valores solicitados antes de confirmar en la aplicación. La revisión aún no ha guardado cambios.",
    "blocked": "Haz una única petición directa para crear, editar o eliminar una transacción, cuenta o presupuesto de Finanzas. No se realizó ningún cambio.",
    "disabled": "La revisión de Finanzas no está disponible temporalmente. No se realizó ningún cambio.",
    "appOnly": "Abre la aplicación VYV para revisar y confirmar cambios de Finanzas.",
    "createdTransaction": "Transacción creada en VYV",
    "updatedTransaction": "Transacción actualizada en VYV",
    "deletedTransaction": "Transacción eliminada de VYV",
    "createdAccount": "Cuenta creada en VYV",
    "updatedAccount": "Cuenta actualizada en VYV",
    "deletedAccount": "Cuenta eliminada de VYV",
    "createdBudget": "Presupuesto creado en VYV",
    "updatedBudget": "Presupuesto actualizado en VYV",
    "deletedBudget": "Presupuesto eliminado de VYV",
    "unchanged": "Los valores revisados ya estaban guardados. No cambió nada.",
    "historical": "Se verificó el resultado original. Esto no confirma el registro actual."
  },
  "fr": {
    "review": "Vérifiez la donnée des Finances sélectionnée et toutes les valeurs demandées avant de confirmer dans l’application. La vérification n’a encore enregistré aucune modification.",
    "blocked": "Faites une seule demande directe pour créer, modifier ou supprimer une transaction, un compte ou un budget des Finances. Aucune modification effectuée.",
    "disabled": "La vérification des Finances est temporairement indisponible. Aucune modification effectuée.",
    "appOnly": "Ouvrez l’application VYV pour vérifier et confirmer les modifications des Finances.",
    "createdTransaction": "Transaction créée dans VYV",
    "updatedTransaction": "Transaction mise à jour dans VYV",
    "deletedTransaction": "Transaction supprimée de VYV",
    "createdAccount": "Compte créé dans VYV",
    "updatedAccount": "Compte mis à jour dans VYV",
    "deletedAccount": "Compte supprimé de VYV",
    "createdBudget": "Budget créé dans VYV",
    "updatedBudget": "Budget mis à jour dans VYV",
    "deletedBudget": "Budget supprimé de VYV",
    "unchanged": "Les valeurs vérifiées étaient déjà enregistrées. Rien n’a changé.",
    "historical": "Le résultat initial a été vérifié. Cela ne confirme pas la donnée actuelle."
  },
  "it": {
    "review": "Controlla il dato delle Finanze selezionato e tutti i valori richiesti prima di confermare nell’app. La revisione non ha ancora salvato modifiche.",
    "blocked": "Fai una sola richiesta diretta per creare, modificare o eliminare una transazione, un conto o un budget delle Finanze. Nessuna modifica effettuata.",
    "disabled": "La revisione delle Finanze è temporaneamente non disponibile. Nessuna modifica effettuata.",
    "appOnly": "Apri l’app VYV per rivedere e confermare le modifiche delle Finanze.",
    "createdTransaction": "Transazione creata in VYV",
    "updatedTransaction": "Transazione aggiornata in VYV",
    "deletedTransaction": "Transazione eliminata da VYV",
    "createdAccount": "Conto creato in VYV",
    "updatedAccount": "Conto aggiornato in VYV",
    "deletedAccount": "Conto eliminato da VYV",
    "createdBudget": "Budget creato in VYV",
    "updatedBudget": "Budget aggiornato in VYV",
    "deletedBudget": "Budget eliminato da VYV",
    "unchanged": "I valori controllati erano già salvati. Non è cambiato nulla.",
    "historical": "Il risultato originale è stato verificato. Questo non conferma il dato attuale."
  },
  "de": {
    "review": "Prüfe den ausgewählten Finanzdatensatz und alle angeforderten Werte, bevor du in der App bestätigst. Die Prüfung hat noch keine Änderungen gespeichert.",
    "blocked": "Bitte direkt um eine einzelne Finanztransaktion, ein Konto oder ein Budget, das erstellt, geändert oder gelöscht werden soll. Es wurde nichts geändert.",
    "disabled": "Die Finanzprüfung ist vorübergehend nicht verfügbar. Es wurde nichts geändert.",
    "appOnly": "Öffne die VYV-App, um Finanzänderungen zu prüfen und zu bestätigen.",
    "createdTransaction": "Transaktion in VYV erstellt",
    "updatedTransaction": "Transaktion in VYV aktualisiert",
    "deletedTransaction": "Transaktion aus VYV gelöscht",
    "createdAccount": "Konto in VYV erstellt",
    "updatedAccount": "Konto in VYV aktualisiert",
    "deletedAccount": "Konto aus VYV gelöscht",
    "createdBudget": "Budget in VYV erstellt",
    "updatedBudget": "Budget in VYV aktualisiert",
    "deletedBudget": "Budget aus VYV gelöscht",
    "unchanged": "Die geprüften Werte waren bereits gespeichert. Es wurde nichts geändert.",
    "historical": "Das ursprüngliche Ergebnis wurde verifiziert. Dies bestätigt nicht den aktuellen Datensatz."
  },
  "pl": {
    "review": "Sprawdź wybrany rekord Finansów i wszystkie żądane wartości przed potwierdzeniem w aplikacji. Przegląd nie zapisał jeszcze żadnych zmian.",
    "blocked": "Wyraź jedną bezpośrednią prośbę o utworzenie, zmianę lub usunięcie transakcji, konta albo budżetu Finansów. Nie dokonano zmian.",
    "disabled": "Przegląd Finansów jest tymczasowo niedostępny. Nie dokonano zmian.",
    "appOnly": "Otwórz aplikację VYV, aby sprawdzić i potwierdzić zmiany Finansów.",
    "createdTransaction": "Transakcja utworzona w VYV",
    "updatedTransaction": "Transakcja zaktualizowana w VYV",
    "deletedTransaction": "Transakcja usunięta z VYV",
    "createdAccount": "Konto utworzone w VYV",
    "updatedAccount": "Konto zaktualizowane w VYV",
    "deletedAccount": "Konto usunięte z VYV",
    "createdBudget": "Budżet utworzony w VYV",
    "updatedBudget": "Budżet zaktualizowany w VYV",
    "deletedBudget": "Budżet usunięty z VYV",
    "unchanged": "Sprawdzone wartości były już zapisane. Nic się nie zmieniło.",
    "historical": "Pierwotny wynik został zweryfikowany. Nie potwierdza to bieżącego rekordu."
  }
} as const satisfies Record<ActiveLangKey, { "review": string; "blocked": string; "disabled": string; "appOnly": string; "createdTransaction": string; "updatedTransaction": string; "deletedTransaction": string; "createdAccount": string; "updatedAccount": string; "deletedAccount": string; "createdBudget": string; "updatedBudget": string; "deletedBudget": string; "unchanged": string; "historical": string }>;

export type MirrorFinanceActionCopy = Readonly<Record<keyof typeof MIRROR_FINANCE_ACTION_COPY.en, string>>;

/** New Finance copy follows the UI English fallback for frozen Bulgarian. */
export function mirrorFinanceActionCopy(lang: LangKey): MirrorFinanceActionCopy {
  return MIRROR_FINANCE_ACTION_COPY[activeLanguageKeyOf(lang)];
}
